import { useEffect, useState } from "react";
import { X, Share2, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * SharePreviewModal — shows a canvas-generated share card before publishing.
 * Props:
 *   open        boolean
 *   blob        Blob | null   (image blob to preview)
 *   text        string        (share text)
 *   url         string        (share url)
 *   onClose()   close without sharing
 */
export default function SharePreviewModal({ open, blob, text, url, onClose }) {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!blob) { setObjectUrl(null); return; }
    const u = URL.createObjectURL(blob);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const shareNow = async () => {
    if (!blob) return;
    const file = new File([blob], "rumore-zero.jpg", { type: "image/jpeg" });
    const withFile = { title: "Radio Rumore Zero", text, url, files: [file] };
    const textOnly = { title: "Radio Rumore Zero", text, url };
    try {
      if (navigator.canShare && navigator.canShare(withFile)) {
        await navigator.share(withFile);
        onClose?.();
        return;
      }
      if (navigator.share && (!navigator.canShare || navigator.canShare(textOnly))) {
        await navigator.share(textOnly);
        onClose?.();
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copiato! Incollalo dove vuoi.");
      onClose?.();
    } catch {
      const wa = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
      window.open(wa, "_blank", "noopener,noreferrer");
      onClose?.();
    }
  };

  const downloadNow = () => {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = "rumore-zero.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Immagine scaricata");
  };

  return (
    <div
      data-testid="share-preview-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-[#332f2b] bg-[#12100e] p-4 sm:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-anton uppercase text-lg tracking-wide text-[#F5F2EB]">Anteprima</h3>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#69635c] mt-0.5">
              Verifica prima di condividere
            </p>
          </div>
          <button
            type="button"
            data-testid="share-preview-close"
            onClick={onClose}
            aria-label="Chiudi anteprima"
            className="p-2 rounded-md border border-[#332f2b] text-[#A39A8F] hover:text-[#F5F2EB] hover:border-[#FF6B00]/50 transition"
          >
            <X size={16} />
          </button>
        </div>

        <div className="rounded-xl overflow-hidden border border-[#332f2b] bg-[#0a0907] aspect-square flex items-center justify-center">
          {objectUrl ? (
            <img
              data-testid="share-preview-image"
              src={objectUrl}
              alt="Anteprima card di condivisione"
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="font-mono text-xs text-[#69635c]">Generazione in corso…</span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="share-preview-download"
            onClick={downloadNow}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-[#332f2b] bg-[#1a1816] text-[#F5F2EB] hover:border-[#FF6B00]/50 hover:text-[#FF6B00] transition font-mono text-[11px] uppercase tracking-[0.18em]"
          >
            <Download size={14} />
            Scarica
          </button>
          <button
            type="button"
            data-testid="share-preview-confirm"
            onClick={shareNow}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-[#FF6B00] bg-[#FF6B00] text-[#12100e] hover:bg-[#ff8226] transition font-mono text-[11px] uppercase tracking-[0.18em] font-semibold shadow-[0_0_20px_rgba(255,107,0,0.35)]"
          >
            <Share2 size={14} />
            Condividi
          </button>
        </div>
      </div>
    </div>
  );
}
